import { type Game } from '@/models/game';
import {
  Permissions,
  WILDCARD,
  grantCovers,
  hasPermission,
  holdsAll,
  type MyRoleGrant,
} from '@/models/permission';

import { isAllSandboxes } from './member';

export interface ManagementScope {
  /** Coverage over every Sandbox: the Game-wide Permission Manager. */
  gameWide: boolean;
  /** The Sandboxes covered, empty when gameWide. */
  sandboxCodes: string[];
}

/**
 * Which Sandboxes of one Game the viewer administers permissions on — the scope
 * banner's only input. Derived from the Grants carrying `users:manage_permissions`,
 * never from a Role name (#419 AC 11).
 *
 * Whether the viewer may act on a *given* scope is not asked here: that is a point
 * check, answered by `usePermissionCheck` / `hasPermission` with the target's
 * game_code and sandbox_code. This function exists for the one question a point
 * check cannot answer — enumerating the scopes held.
 */
export const managementScope = (
  grants: MyRoleGrant[],
  gameCode: string,
): ManagementScope => {
  // Sandbox left unconstrained: managing any Sandbox of the Game opens the Access
  // area; the Sandbox restriction applies to what may be edited, not to visibility.
  const managing = grants.filter((grant) =>
    grantCovers(grant, Permissions.users.managePermissions, gameCode),
  );
  const gameWide = managing.some(
    ({ sandbox_code }) => sandbox_code === WILDCARD,
  );

  return {
    gameWide,
    sandboxCodes: gameWide
      ? []
      : [...new Set(managing.map(({ sandbox_code }) => sandbox_code))].sort(),
  };
};

/** "All Sandboxes" / "QA Sandbox only" / "QA, DEV Sandboxes only". */
export const managementScopeLabel = ({
  gameWide,
  sandboxCodes,
}: ManagementScope) => {
  if (gameWide) return 'All Sandboxes';
  if (sandboxCodes.length === 1) return `${sandboxCodes[0]} Sandbox only`;
  return `${sandboxCodes.join(', ')} Sandboxes only`;
};

/**
 * Whether this Grant carries `users:manage_permissions` — the viewer may never
 * revoke one of their own (#419 AC 9), whether or not it is their last.
 *
 * Which Grants count as permission-management is derived from the permissions they
 * carry, never from the Role name (AC 11); the name only ever reaches the copy.
 * admin-api enforces the same rule and stays authoritative
 * (srvc/permission/grant_service.go, guardSelfLockout).
 */
export const isManagementGrant = (grant: MyRoleGrant) =>
  grantCovers(grant, Permissions.users.managePermissions);

const FULL_WILDCARD = { resource: WILDCARD, action: WILDCARD };

/**
 * Whether the viewer may remove `grant` as far as BR-RBAC-70 goes: a Grant
 * conferring `*:*` — the administrator Role today, recognised by its permissions
 * and never by its name — only yields to a viewer holding `*:*` on a scope
 * containing its own. Any other Grant is unaffected. admin-api enforces the same
 * rule and stays authoritative.
 */
export const canRemoveAdministratorGrant = (
  viewer: MyRoleGrant[],
  grant: MyRoleGrant,
) =>
  !grant.permissions.some(
    ({ resource, action }) => resource === WILDCARD && action === WILDCARD,
  ) ||
  holdsAll(
    viewer,
    [FULL_WILDCARD],
    grant.game_code,
    grant.sandbox_code,
    grant.studio_code,
  );

/**
 * The single mechanism for every "not yours to remove" case: an empty string
 * means the Grant can be removed, anything else is the explanation shown instead
 * of removing it.
 */
export const removalReason = (
  myGrants: MyRoleGrant[],
  gameCode: string,
  isSelf: boolean,
) => {
  const scope = managementScope(myGrants, gameCode);

  return (grant: MyRoleGrant) => {
    const inScope = hasPermission(
      myGrants,
      Permissions.users.managePermissions,
      grant.game_code,
      grant.sandbox_code,
      grant.studio_code,
    );

    if (inScope) {
      // #419 AC 9. admin-api refuses this too, so the UI is the explanation, not
      // the rule.
      if (isSelf && isManagementGrant(grant)) {
        return `The ${grant.name} Grant carries permission management. You cannot revoke your own.`;
      }

      // BR-RBAC-70: managing permissions on the scope is not enough here.
      // Any other Grant is bounded by the scope alone (BR-RBAC-60): holding the
      // Role it confers is not required to remove it.
      return canRemoveAdministratorGrant(myGrants, grant)
        ? ''
        : `The ${grant.name} Grant confers every permission. Only an Administrator on this scope can remove it.`;
    }

    // ponytail: the mockups have no cross-Game Grant, so this sentence is ours —
    // a ('*', '*') Grant is administered by a Nacon-wide manager only, and
    // "outside the Sandboxes you manage" would misdescribe it.
    if (grant.game_code === WILDCARD) {
      return 'This Grant covers every Game. Only a Nacon-wide Permission Manager can remove it.';
    }

    // The Game, not the Sandbox, is what is out of reach here: naming the Sandbox
    // scope would read as a contradiction to a Game-wide manager of another Game.
    if (grant.game_code !== gameCode) {
      return `This Grant is on ${grant.game_code}. You administer access on ${gameCode} only.`;
    }

    return `Outside your scope — you manage ${managementScopeLabel(scope)}.`;
  };
};

// What the sheet is asking to confirm. 'blocked' is the odd one out: it confirms
// nothing, it explains why the removal the viewer just asked for is unavailable.
//
// One row or twelve is the same shape: the per-row Remove button asks about a list
// of one, so there is one removal path rather than two that can drift.
export type Confirmation =
  | { kind: 'removeGrants'; grants: MyRoleGrant[] }
  | { kind: 'blocked'; reason: string };

/** The first Grant asked that is not removable blocks the whole batch. */
export const confirmationFor = (
  asked: MyRoleGrant[],
  reason: (grant: MyRoleGrant) => string,
): Confirmation => {
  const blocked = asked.map(reason).find((why) => why !== '');
  return blocked === undefined
    ? { kind: 'removeGrants', grants: asked }
    : { kind: 'blocked', reason: blocked };
};

/** One Game's worth of the viewer's Grants — a tab. */
export interface GameGroup {
  gameCode: string;
  label: string;
  grants: MyRoleGrant[];
}

/**
 * The viewer's Grants bucketed per Game, broadest first, then by the name actually
 * shown so the order matches the labels rather than the codes behind them.
 *
 * The label falls back to the raw game_code: /v1/games may be slow, may fail, or may
 * omit a Game the viewer holds a Grant on, and none of that should blank a tab.
 */
export const groupByGame = (
  grants: MyRoleGrant[],
  games: Game[],
): GameGroup[] => {
  const buckets = new Map<string, MyRoleGrant[]>();
  for (const grant of grants) {
    buckets.set(grant.game_code, [
      ...(buckets.get(grant.game_code) ?? []),
      grant,
    ]);
  }

  return [...buckets]
    .map(([gameCode, gameGrants]) => ({
      gameCode,
      label:
        gameCode === WILDCARD
          ? 'All Games'
          : (games.find((game) => game.game_code === gameCode)?.name ??
            gameCode),
      grants: gameGrants,
    }))
    .sort((a, b) => {
      if (a.gameCode === WILDCARD) return -1;
      if (b.gameCode === WILDCARD) return 1;
      return a.label.localeCompare(b.label);
    });
};

// Broadest first, then alphabetical: 'All Sandboxes' is the widest scope a Grant can
// carry, so it heads the list instead of sorting under 'A'.
export const byScope = (a: MyRoleGrant, b: MyRoleGrant) => {
  if (isAllSandboxes(a) !== isAllSandboxes(b))
    return isAllSandboxes(a) ? -1 : 1;
  return a.sandbox_code.localeCompare(b.sandbox_code);
};

/**
 * One row per Role, holding every Sandbox it is granted on.
 *
 * A Role is defined once in the catalogue, so two Grants naming it carry the same
 * description and the same permissions: a row per Grant repeated the tallest cell of
 * the table once per Sandbox. `grant` is the Role's identity — any Grant of the group
 * answers for it — and `scopes` is what actually differs.
 *
 * game_code stays out of the key: it is already constant within a tab.
 */
export const groupByRole = (grants: MyRoleGrant[]) => {
  const buckets = new Map<
    string,
    { grant: MyRoleGrant; scopes: MyRoleGrant[] }
  >();
  for (const grant of grants) {
    const bucket = buckets.get(grant.id);
    if (bucket) bucket.scopes.push(grant);
    else buckets.set(grant.id, { grant, scopes: [grant] });
  }

  // Insertion order is the API's order — nothing here invents a ranking of Roles.
  return [...buckets.values()].map(({ grant, scopes }) => ({
    grant,
    scopes: [...scopes].sort(byScope),
  }));
};
